import { createContext, useContext } from "react";

export interface CreateProjectDialogContextValue {
  /**
   * Open the shared dialog. What happens with the project it creates is the
   * provider's, wired from the building layout — every picker wants the same
   * follow-through (scope to it), and a picker can unmount between the click
   * and the create resolving, which is exactly when a callback of its own
   * would write against the location it last saw.
   */
  openCreateProject: () => void;
  /**
   * Whether the user may create projects at all. Callers gate their own
   * affordance on this so nobody is offered a dialog they cannot use.
   */
  canCreateProjects: boolean;
}

/** No provider above us: there is no dialog to open. */
function noDialogToOpen(): void {
  return undefined;
}

export const CreateProjectDialogContext = createContext<CreateProjectDialogContextValue>({
  openCreateProject: noDialogToOpen,
  canCreateProjects: false,
});

/**
 * Reach the shared create-project dialog (see CreateProjectDialogProvider).
 * Outside a provider `canCreateProjects` reads false and the caller hides its
 * "New project" affordance — the same shape as a user without the permission,
 * rather than a button that does nothing.
 */
export function useCreateProjectDialog(): CreateProjectDialogContextValue {
  return useContext(CreateProjectDialogContext);
}
